import { useCallback, useEffect, useMemo, useState } from "react";
import { StatCardsRow } from "@/components/StatCard/StatCard";
import type { StatCardData } from "@/components/StatCard/StatCard";
import Button from "@/components/Button/Button";
import UsersIcon from "@/components/SVGComponents/UsersIcon";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import DownloadIcon from "@/components/SVGComponents/DownloadIcon";
import Table from "@/components/Table/Table";
import PastEventCard from "./components/PastEventCard";
import NoDataFound from "@/components/NoDataFound/NoDataFound";
import type { Column } from "@/types/common";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getCommentReports, hideComment, dismissComment } from "@/store/slices/commentReportSlice";
import type { FlaggedComment } from "@/services/api/commentReports";
import { getPastEvents, createPastEvent, exportPastEvents } from "@/services/api/pastEvents";
import type { CreatePastEventPayload, PastEvent, PastEventsResponse } from "@/services/api/pastEvents";
import type { EventType } from "@/services/api/events";
import { EVENT_TYPE_LABELS, formatEventDate, formatTicketPrice } from "@/pages/Gatherings/lib/eventFormat";
import toast from "@/lib/toast";
import { TableCellTooltip } from "@/components/Tooltip";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import ExportConfirmationModal from "@/components/ExportConfirmationModal/ExportConfirmationModal";
import AddPastEventModal from "./components/AddPastEventModal/AddPastEventModal";
import ReportDetailsModal from "./components/ReportDetailsModal/ReportDetailsModal";
import Pagination from "@/components/Table/Pagination";
import styles from "./PastEvents.module.scss";

interface PastEventsStatsData {
  totalPublishedEvents: number;
  totalLikesCount: number;
  totalCommentsCount: number;
  totalFlaggedComments: number;
}

const DEFAULT_STATS: PastEventsStatsData = {
  totalPublishedEvents: 0,
  totalLikesCount: 0,
  totalCommentsCount: 0,
  totalFlaggedComments: 0,
};

const ROWS_PER_PAGE_COMMENT_REPORTS = 10;
const ROWS_PER_PAGE = 10;

const getPastEventCity = (row: PastEvent) => {
  if (row.cityName) return row.cityName;
  if (typeof row.city === "string" && row.city) return row.city;
  if (row.locations && row.locations.length > 0 && row.locations[0]?.cityName) {
    return row.locations[0].cityName;
  }
  if (row.cities && row.cities.length > 0 && row.cities[0]) {
    return row.cities[0];
  }
  return "—";
};

const COLUMNS: Column<PastEvent>[] = [
  {
    name: "Event",
    width: "22%",
    cell: (row) => <TableCellTooltip value={row.name || "—"} textClassName={styles.eventName} />,
  },
  {
    name: "City",
    width: "16%",
    selector: (row) => getPastEventCity(row),
  },
  {
    name: "Date",
    width: "16%",
    selector: (row) => {
      const date = row.eventDate ?? row.date;
      return date ? formatEventDate(date) : "—";
    },
  },
  {
    name: "Attendance",
    width: "15%",
    selector: (row) => {
      const attendance = row.attended ?? row.attendance ?? row.capacity;
      return attendance !== undefined && attendance !== null && attendance !== "" ? String(attendance) : "—";
    },
  },
  {
    name: "Likes",
    width: "15%",
    cell: (row) => (row.likesCount !== undefined && row.likesCount !== null ? row.likesCount : "—"),
  },
  {
    name: "Comments",
    width: "15%",
    cell: (row) => (row.commentsCount !== undefined && row.commentsCount !== null ? row.commentsCount : "—"),
  },
];

const PastEvents = () => {
  const dispatch = useAppDispatch();
  const [pastEvents, setPastEvents] = useState<PastEvent[]>([]);
  const [totalPastEvents, setTotalPastEvents] = useState(0);
  const [isFetchingPastEvents, setIsFetchingPastEvents] = useState(false);
  const [statsData, setStatsData] = useState<PastEventsStatsData>(DEFAULT_STATS);
  const {
    commentReports,
    totalCommentReports,
    isFetching: isFetchingCommentReports,
    error: commentReportsError,
  } = useAppSelector((state) => state.commentReport);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [pageSelected, setPageSelected] = useState(1);
  const [commentReportsPage, setCommentReportsPage] = useState(1);
  // A single pending-action slot rather than separate hide/dismiss state —
  // only one of these two actions can be in confirmation at a time per row,
  // and both share the same ConfirmDeleteModal instance below.
  const [pendingCommentAction, setPendingCommentAction] = useState<{
    comment: FlaggedComment;
    type: "hide" | "dismiss";
  } | null>(null);
  const [viewingReportsComment, setViewingReportsComment] = useState<FlaggedComment | null>(null);

  const fetchPastEvents = useCallback(async () => {
    setIsFetchingPastEvents(true);
    try {
      const res = await getPastEvents({ pageNo: pageSelected, perPage: ROWS_PER_PAGE });
      const payload: PastEventsResponse | PastEvent[] = res?.data ?? res;
      if (Array.isArray(payload)) {
        setPastEvents(payload);
        setTotalPastEvents(payload.length);
        setStatsData((prev) => ({
          ...prev,
          totalPublishedEvents: payload.length,
        }));
      } else if (payload && typeof payload === "object") {
        const results = Array.isArray(payload.results) ? payload.results : [];
        setPastEvents(results);
        setTotalPastEvents(payload.totalRecord ?? payload.totalPublishedEvents ?? results.length);
        setStatsData({
          totalPublishedEvents: payload.totalPublishedEvents ?? results.length,
          totalLikesCount: payload.totalLikesCount ?? 0,
          totalCommentsCount: payload.totalCommentsCount ?? 0,
          totalFlaggedComments: payload.totalFlaggedComments ?? 0,
        });
      } else {
        setPastEvents([]);
        setTotalPastEvents(0);
      }
    } catch {
      setPastEvents([]);
      setTotalPastEvents(0);
    } finally {
      setIsFetchingPastEvents(false);
    }
  }, [pageSelected]);

  useEffect(() => {
    fetchPastEvents();
  }, [fetchPastEvents]);

  const stats: StatCardData[] = useMemo(
    () => [
      { label: "Published Events", value: statsData.totalPublishedEvents, icon: <UsersIcon /> },
      { label: "Member Comments", value: statsData.totalCommentsCount, icon: <UsersIcon /> },
      { label: "Flagged Comments", value: statsData.totalFlaggedComments, icon: <UsersIcon /> },
      { label: "Likes", value: statsData.totalLikesCount, icon: <UsersIcon /> },
    ],
    [statsData],
  );

  const handleCreatePastEvent = async (payload: FormData) => {
    try {
      await createPastEvent(payload);
      // New events land on page 1 — switching pages triggers the refetch
      // via the effect; if already there, refetch directly.
      if (pageSelected === 1) await fetchPastEvents();
      else setPageSelected(1);
      toast.success("Past event created successfully.");
    } catch (error) {
      toast.error("Failed to create past event. Please try again.");
      throw error;
    }
  };

  const fetchCommentReports = useCallback(() => {
    dispatch(getCommentReports({ pageNo: commentReportsPage, perPage: ROWS_PER_PAGE_COMMENT_REPORTS }));
  }, [dispatch, commentReportsPage]);

  useEffect(() => {
    fetchCommentReports();
  }, [fetchCommentReports]);

  // Hide/dismiss drop the row locally, leaving the page one short. Refetch
  // so the next comment moves up — or step back a page if that was the
  // last row on a page past the first.
  const refreshCommentReportsAfterRemoval = () => {
    if (commentReports.length === 1 && commentReportsPage > 1) {
      setCommentReportsPage((page) => page - 1);
    } else {
      fetchCommentReports();
    }
  };

  const handleHideComment = async (comment: FlaggedComment) => {
    try {
      await dispatch(hideComment(comment.commentId)).unwrap();
      refreshCommentReportsAfterRemoval();
      toast.success(`${comment.commentAuthorName}'s comment was hidden.`);
    } catch {
      toast.error("Failed to hide the comment. Please try again.");
    }
  };

  const handleDismissComment = async (comment: FlaggedComment) => {
    try {
      await dispatch(dismissComment(comment)).unwrap();
      refreshCommentReportsAfterRemoval();
      toast.success("Report dismissed.");
    } catch {
      toast.error("Failed to dismiss the report. Please try again.");
    }
  };

  const handleConfirmPendingCommentAction = () => {
    if (!pendingCommentAction) return;
    const { comment, type } = pendingCommentAction;
    if (type === "hide") {
      handleHideComment(comment);
    } else {
      handleDismissComment(comment);
    }
  };

  usePageSubtitle(
    isFetchingPastEvents
      ? "Loading past events…"
      : `${totalPastEvents} past gathering${totalPastEvents === 1 ? "" : "s"}`,
  );

  // `isFetching` alone can't tell a first load apart from a background
  // refetch — same convention as Subscriptions.tsx: render nothing (no
  // spinner) until there's either real data or an error to show, so a
  // retry after an error doesn't blank an already-populated list.
  const isInitialCommentReportsLoad = isFetchingCommentReports && commentReports.length === 0;

  let flaggedCommentsContent = null;
  if (isInitialCommentReportsLoad) {
    // Nothing rendered while the first fetch is in flight — no spinner.
  } else if (commentReportsError) {
    flaggedCommentsContent = (
      <div className={styles.flaggedStateBox}>
        <p className={styles.stateTitle}>Couldn&apos;t load flagged comments</p>
        <p className={styles.stateCaption}>{commentReportsError}</p>
        <Button type="button" onClick={fetchCommentReports}>
          Try again
        </Button>
      </div>
    );
  } else if (commentReports.length === 0) {
    flaggedCommentsContent = <NoDataFound itemName="flagged comment" />;
  } else {
    flaggedCommentsContent = (
      <div className={styles.flaggedList}>
        {commentReports.map((comment) => (
          <div key={comment.commentId} className={styles.flaggedRow}>
            <div className={styles.flaggedContent}>
              <div className={styles.flaggedHeader}>
                <span className={styles.flaggedName}>
                  {comment.commentAuthorName || "-"}
                </span>
                {comment.eventName && (
                  <span className={styles.flaggedEventName}>
                    <span className={styles.eventDot}> · </span>
                    {comment.eventName}
                  </span>
                )}
              </div>
              <p className={styles.flaggedQuote}>&quot;{comment.commentText || "-"}&quot;</p>
            </div>

            <div className={styles.flaggedActions}>
              <button
                type="button"
                className={styles.reportCount}
                onClick={() => setViewingReportsComment(comment)}
              >
                {comment.reportCount} report{comment.reportCount === 1 ? "" : "s"}
              </button>
              <Button
                outlined
                className={styles.hideButton}
                onClick={() => setPendingCommentAction({ comment, type: "hide" })}
              >
                HIDE COMMENT
              </Button>
              <Button
                outlined
                className={styles.dismissButton}
                onClick={() => setPendingCommentAction({ comment, type: "dismiss" })}
              >
                DISMISS
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <StatCardsRow stats={stats} />

      {/* Desktop View (100% strictly preserved existing UI) */}
      <div className={styles.desktopView}>
        <Table<PastEvent>
          columns={COLUMNS}
          data={pastEvents}
          progressPending={isFetchingPastEvents}
          // A fixed, modest height rather than the near-full-viewport
          // `calc(100vh - 210px)` Members.tsx/Support.tsx/TeamRoles.tsx use —
          // those pages are just one dominant table; this one sits between
          // the stat cards above and the "Flagged Comments" card below, both
          // of which need to stay visible in normal page flow rather than
          // getting pushed off past a table tall enough to fill the screen.
          containerHeight="560px"
          header={{
            titleName: "Past Events",
            showSearch: false,
            addButtonTitle: "ADD PAST EVENT",
            onAddClick: () => setIsAddModalOpen(true),
            onExport: () => setIsExportModalOpen(true),
          }}
          paginationProps={{
            isPagination: true,
            pageSelected,
            totalCount: totalPastEvents,
            rowsPerPageValue: ROWS_PER_PAGE,
            setPageSelected,
          }}
        />
      </div>

      {/* Mobile View (Simple, sober card-based UI for mobile screens <= 768px only) */}
      <div className={styles.mobileView}>
        <div className={styles.mobileToolbar}>
          <h2 className={styles.mobileTitle}>Past Events ({totalPastEvents})</h2>
          <div className={styles.mobileToolbarActions}>
            <Button
              outlined
              className={styles.mobileExportBtn}
              onClick={() => setIsExportModalOpen(true)}
            >
              <DownloadIcon />
              <span>EXPORT CSV</span>
            </Button>
            <Button
              className={styles.mobileAddBtn}
              onClick={() => setIsAddModalOpen(true)}
            >
              <PlusIcon />
              <span>ADD PAST EVENT</span>
            </Button>
          </div>
        </div>

        <div className={styles.mobileEventCardList}>
          {isFetchingPastEvents ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className={styles.mobileSkeletonCard}>
                <div className={styles.skeletonHeader}>
                  <div className={styles.skeletonLineShort} />
                  <div className={styles.skeletonBadge} />
                </div>
                <div className={styles.skeletonLineMeta} />
                <div className={styles.skeletonDivider} />
                <div className={styles.skeletonGrid}>
                  <div className={styles.skeletonLine} />
                  <div className={styles.skeletonLine} />
                </div>
              </div>
            ))
          ) : pastEvents.length === 0 ? (
            <div className={styles.mobileEmptyState}>
              <h4 className={styles.mobileEmptyTitle}>No past events found</h4>
            </div>
          ) : (
            pastEvents.map((event, idx) => <PastEventCard key={event.id ?? idx} event={event} />)
          )}
        </div>

        {totalPastEvents > ROWS_PER_PAGE && (
          <div className={styles.mobilePaginationContainer}>
            <Pagination
              totalCount={totalPastEvents}
              pageSelected={pageSelected}
              rowsPerPageValue={ROWS_PER_PAGE}
              setPageSelected={setPageSelected}
            />
          </div>
        )}
      </div>

      <AddPastEventModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onCreate={handleCreatePastEvent}
      />

      <ExportConfirmationModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title="Export past events"
        fallbackFileName="past-events"
        fetchExportFile={() => exportPastEvents()}
      />

      <div className={styles.card}>
        <h2 className={styles.title}>Flagged Comments</h2>
        <p className={styles.flaggedSubtitle}>
          Reported by members. Hide removes it from the app, dismiss clears the report.
        </p>

        {flaggedCommentsContent}

        {/* Only shown once there is more than one page (> 10 flagged comments). */}
        {!isInitialCommentReportsLoad &&
          !commentReportsError &&
          commentReports.length > 0 &&
          totalCommentReports > ROWS_PER_PAGE_COMMENT_REPORTS && (
            <div className={styles.flaggedPagination}>
              <Pagination
                totalCount={totalCommentReports}
                pageSelected={commentReportsPage}
                rowsPerPageValue={ROWS_PER_PAGE_COMMENT_REPORTS}
                setPageSelected={setCommentReportsPage}
              />
            </div>
          )}
      </div>

      <ConfirmDeleteModal
        isOpen={pendingCommentAction !== null}
        onClose={() => setPendingCommentAction(null)}
        onConfirm={handleConfirmPendingCommentAction}
        headerTitle={pendingCommentAction?.type === "hide" ? "Confirm Hide" : "Confirm Dismiss"}
        confirmLabel={pendingCommentAction?.type === "hide" ? "Hide" : "Dismiss"}
        title={pendingCommentAction?.type === "hide" ? "Hide Comment" : "Dismiss Report"}
        message={
          pendingCommentAction?.type === "hide" ? (
           <>
      <span>
        Are you sure you want to hide{" "}
        <strong>{pendingCommentAction.comment.commentAuthorName}</strong>&apos;s
        comment?
      </span>

      <span>
        This will remove it, including its replies and reports.</span>
    </>
          ) : (
            "Are you sure you want to dismiss this report? The comment will remain visible."
          )
        }
      />

      <ReportDetailsModal
        isOpen={viewingReportsComment !== null}
        onClose={() => setViewingReportsComment(null)}
        comment={viewingReportsComment}
      />
    </div>
  );
};

export default PastEvents;
